import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastMessageOptions } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';

import { Features } from '../../../models/models';

import { 
         UserAuthorizationService,
         IFeaturesService,
         IApplicationContextService,
         IOpsViewerMessagesService } from '../../../services/services';
import { Title } from '@angular/platform-browser';
import { IUserManagerService } from '../../../services/iUserManager.service';
import { Company, CompanyClient, User } from '../../../services/nswag/nswagclient';

type MessageSeverity = 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'company-list',
    templateUrl: './company-list.component.html',
    styleUrls: ['./company-list.component.css'],
    standalone: false
})
export class CompanyListComponent implements OnInit {

  cols: any[] = [];
  companiesReady = false;
  companies: Company[] = [];
  categories: string[] = [];
  messages: ToastMessageOptions[] = [];
  isCompanyAdministrationEnabled = false;
  featuresReady = false;
  user: User | null = null;
  isUserReady = false;
  everythingReady = false;

  constructor(
    private _companyService: CompanyClient,
    private _userAuthorizationService: UserAuthorizationService,
    private _router: Router,
    private _confirmationService: ConfirmationService,
    private _featureService: IFeaturesService,
    private _applicationContextService: IApplicationContextService,
    private _messageService: IOpsViewerMessagesService,
    private _userManagerService:IUserManagerService,
    private _titleService: Title ) { }

  ngOnInit() {
    this.setPageTitle();
    this.setColumns();
    this.getCompanies();
    this.getFeatures();
    this.getAuthenticatedUser();
  }

  setPageTitle() {
    this._titleService.setTitle('OpsViewer Companies');
  }

  setColumns() {
    this.cols = [
      {field: 'name', header: 'Name', width: '250px'},
      {field: 'prefix', header: 'Prefix', width: '50px'},
      {field: 'companyType', header:'Type', width:'50px'},
      {field: '', header: '', width: '60px'}
    ];
  }

  getCompanies() {
    this.messages = [];
    this._companyService.get().subscribe({
      next: result => {
        this.companies = result;
        this.populateCategories();
        this.companiesReady = true;
        this.checkIfEverythingReady();
      },
      error: error => {
        this.handleError(error, 'Could not get companies');
      }
    });
  }
  

  getFeatures() {
    this._featureService.isFeatureEnabled(Features.CompanyAdministration).subscribe(
      result => {
        this.isCompanyAdministrationEnabled = result;
        this.featuresReady = true;
        this.checkIfEverythingReady();
      },
      error => { this.handleError(error, 'Could not get features'); }
    );
  }


  getAuthenticatedUser() {

    this._userManagerService.getCurrentUser().subscribe(u=>{
      this.user = u;
      this.isUserReady = true;
      this.checkIfEverythingReady();
    });
  }


  checkIfEverythingReady() {
    this.everythingReady = this.featuresReady && this.companiesReady && this.isUserReady;
  }

  isAdmin(company: Company): boolean {
      if (!this.user || !company.id) {
        return false;
      }
      return this._userAuthorizationService.isUserAdmin(this.user, company.id, false);
  }
  isGlobalAdmin(): boolean {
    return !!this.user?.isGlobalAdmin;
  }

  populateCategories() {
    this.companies.forEach(company => {
      if (company.category && !this.categories.includes(company.category)) {
        this.categories.push(company.category);
      }
    });
  }

  navigateToAddCompany() {
    this._router.navigate(['/company/' + encodeURIComponent('new')]);
  }

  navigateToDetail(companyId: string) {
    this._router.navigate(['/company/' + encodeURIComponent(companyId)]);
  }

  refresh() {
    this.cols = [];
    this.companiesReady = false;
    this.companies = [];
    this.categories = [];
    this.messages = [];
    this.ngOnInit();
  }

  confirmDelete(company: Company) {
    if (!company.id) {
      return;
    }
    this._confirmationService.confirm(
      {
        key: 'confirmationDialog',
        header: 'Delete Confirmation',
        message: 'Are you sure you want to delete ' + company.name + '?' +
                 '  Deleting Company will also delete all related records to this company.',
        icon: 'fa fa-question-circle',
        accept: () => { this.deleteCompany(company.id); }// ,
        // reject: () => { }
      }
    );
  }

  deleteCompany(companyId: string | undefined) {
    if (!companyId) {
      return;
    }
    this._companyService.delete(companyId).subscribe(
      result => {
        this.refresh();
        this._messageService.infoMessage('Company Deleted', '');
        this._applicationContextService.alertCompaniesListUpdated();
      },
      error => {this.handleError(error, 'Could not Delete Company'); }
    );
  }

  handleError(error: any, displayMessage: string) {
    this._messageService.errorMessage(error, displayMessage);
  }

  asMessageSeverity(severity: ToastMessageOptions['severity'] | undefined): MessageSeverity | undefined {
    return severity as MessageSeverity | undefined;
  }

}
